import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Upload,
  Camera,
  Image as ImageIcon,
  Loader2,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Eye,
  Download,
  Printer,
  ZoomIn,
  ZoomOut,
  Search,
  SlidersHorizontal,
  ChevronRight,
  TrendingUp,
  Store,
  FileSpreadsheet,
  Check,
  RefreshCw,
  Sparkles,
  Info,
  X
} from "lucide-react";
import { toast } from "react-hot-toast";
import EmptyState from "../components/EmptyState";
import authAxios from "../api/authAxios";
import { useNavigate } from "react-router-dom";
import { resolveMediaUrl } from "../utils/resolveMediaUrl";

export default function SmartPurchaseBillPage() {
  const navigate = useNavigate();

  // Generate Purchase Invoice States
  const [invoiceModalBill, setInvoiceModalBill] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [deleteConfirmBill, setDeleteConfirmBill] = useState(null);

  const openGenerateInvoiceModal = (bill) => {
    setInvoiceModalBill(bill);
    setInvoiceForm({
      supplier_name: bill.supplier_name || "",
      invoice_number: bill.invoice_number || "",
      bill_date: bill.bill_date || "",
      gst_number: bill.gst_number || "",
      notes: bill.notes || "",
      products: (bill.products || []).map(p => ({
        name: p.name || "",
        quantity: p.quantity || 1,
        purchase_price: p.purchase_price || 0,
        tax_rate: p.tax_rate !== undefined ? p.tax_rate : 18,
        hsn: p.hsn || "-",
        asn_code: p.asn_code || ""
      })),
      total_amount: bill.total_amount || 0
    });
  };

  const handleInvoiceItemChange = (idx, field, value) => {
    const updated = [...invoiceForm.products];
    updated[idx][field] = value;
    
    // Recalculate subtotal and grand total dynamically
    let newSubtotal = 0;
    let newTax = 0;
    updated.forEach(item => {
      const q = parseFloat(item.quantity) || 0;
      const p = parseFloat(item.purchase_price) || 0;
      const t = parseFloat(item.tax_rate) || 0;
      const val = q * p;
      newSubtotal += val;
      newTax += val * (t / 100);
    });
    const newTotal = newSubtotal + newTax;
    
    setInvoiceForm(prev => ({
      ...prev,
      products: updated,
      total_amount: newTotal.toFixed(2)
    }));
  };

  const addInvoiceItem = () => {
    setInvoiceForm(prev => {
      const updated = [...prev.products, { name: "", quantity: 1, purchase_price: 0, tax_rate: 18, hsn: "-", asn_code: "" }];
      return { ...prev, products: updated };
    });
  };

  const removeInvoiceItem = (idx) => {
    setInvoiceForm(prev => {
      const updated = prev.products.filter((_, i) => i !== idx);
      let newSubtotal = 0;
      let newTax = 0;
      updated.forEach(item => {
         const q = parseFloat(item.quantity) || 0;
         const p = parseFloat(item.purchase_price) || 0;
         const t = parseFloat(item.tax_rate) || 0;
         const val = q * p;
         newSubtotal += val;
         newTax += val * (t / 100);
      });
      const newTotal = newSubtotal + newTax;
      return { ...prev, products: updated, total_amount: newTotal.toFixed(2) };
    });
  };

  const handleGenerateInvoiceSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceForm.supplier_name || !invoiceForm.invoice_number || !invoiceForm.total_amount) {
      toast.error("Please fill in Supplier Name, Invoice Number, and Total Amount.");
      return;
    }
    
    try {
      setGeneratingInvoice(true);
      const res = await authAxios.post(`/api/purchase/smart-bills/${invoiceModalBill.id}/generate-invoice/`, invoiceForm);
      toast.success("Purchase Invoice generated successfully!");
      
      // Cache generated invoice in local storage
      localStorage.setItem("latest_purchase_request", JSON.stringify(res.data.invoice_data));
      
      // Close modal
      setInvoiceModalBill(null);
      
      // Refresh list
      fetchData();
      
      // Redirect to print / view template
      navigate("/pos/invoice-success", { state: { ...res.data.invoice_data, isPurchaseInvoice: true } });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to generate purchase invoice.");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const viewGeneratedInvoice = async (bill) => {
    try {
      toast.loading("Fetching invoice data...", { id: "fetch-inv" });
      const res = await authAxios.get(`/api/purchase/requests/${bill.generated_invoice_id}/`);
      toast.dismiss("fetch-inv");
      
      localStorage.setItem("latest_purchase_request", JSON.stringify(res.data.invoice_data));
      navigate("/pos/invoice-success", { state: { ...res.data.invoice_data, isPurchaseInvoice: true } });
    } catch (err) {
      toast.dismiss("fetch-inv");
      console.error(err);
      toast.error("Failed to fetch the generated purchase invoice.");
    }
  };

  const handleDeleteBill = (bill) => {
    setDeleteConfirmBill(bill);
  };

  const confirmDeleteBill = async () => {
    if (!deleteConfirmBill) return;
    const billId = deleteConfirmBill.id;
    setDeleteConfirmBill(null);
    
    try {
      toast.loading("Deleting record...", { id: "delete-bill" });
      await authAxios.delete(`/api/purchase/smart-bills/${billId}/`);
      toast.success("Purchase bill record deleted successfully!", { id: "delete-bill" });
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to delete the purchase bill record.", { id: "delete-bill" });
    }
  };

  // Analytics State
  const [analytics, setAnalytics] = useState({
    monthly_purchase_total: 0,
    total_uploaded_bills: 0,
    most_used_supplier: "N/A"
  });

  // Scanned Bills List State
  const [bills, setBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(true);

  // Search & Filter State
  const [search, setSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterAmount, setFilterAmount] = useState("");
  const [filterGst, setFilterGst] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // OCR & Form State
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingOcr, setIsProcessingOcr] = useState(false);
  const [ocrCompleted, setOcrCompleted] = useState(false);
  const [convertInventory, setConvertInventory] = useState(true);

  // Current Form Data (Extracted/Edited)
  const [formData, setFormData] = useState({
    supplier_name: "",
    invoice_number: "",
    bill_date: "",
    total_amount: "",
    gst_number: "",
    products: [],
    notes: "",
    file_url: "",
    original_extracted_text: "",
    original_extracted_data: {}
  });

  // Modal State for Previewing
  const [previewBill, setPreviewBill] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  // File Input Ref
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Fetch Bills and Analytics
  const fetchData = async () => {
    try {
      setLoadingBills(true);
      // Fetch bills
      const params = {};
      if (search) params.supplier_name = search;
      if (filterDate) params.bill_date = filterDate;
      if (filterAmount) params.total_amount = filterAmount;
      if (filterGst) params.gst_number = filterGst;

      const billsRes = await authAxios.get("/api/purchase/smart-bills/", { params });
      setBills(billsRes.data);

      // Fetch analytics
      const analyticsRes = await authAxios.get("/api/purchase/smart-bills/analytics/");
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load purchase records.");
    } finally {
      setLoadingBills(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search, filterDate, filterAmount, filterGst]);

  // Handle Drag & Drop Events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFileUpload(files[0]);
    }
  };

  // Trigger file uploads based on choices
  const triggerUpload = (type) => {
    setShowUploadOptions(false);
    if (type === "camera") {
      cameraInputRef.current.click();
    } else {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      processFileUpload(files[0]);
    }
  };

  // Upload file & Run OCR
  const processFileUpload = async (file) => {
    const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    const fileExtension = file.name ? file.name.split('.').pop().toLowerCase() : "";
    const validExtensions = ["jpg", "jpeg", "png", "webp", "pdf"];
    const isValid = validTypes.includes(file.type) || validExtensions.includes(fileExtension);
    
    if (!isValid) {
      toast.error("Invalid format! Please upload an Image or PDF.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large! Maximum limit is 10MB.");
      return;
    }

    // Start Simulation of Upload Progress
    setOcrCompleted(false);
    setUploadProgress(10);
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 15;
      });
    }, 200);

    // Call API for OCR
    const formDataObj = new FormData();
    formDataObj.append("file", file);

    try {
      setIsProcessingOcr(true);
      const res = await authAxios.post("/api/purchase/smart-bills/ocr/", formDataObj, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Pre-fill form fields
      setFormData({
        supplier_name: res.data.supplier_name || "",
        invoice_number: res.data.invoice_number || "",
        bill_date: res.data.bill_date || "",
        total_amount: res.data.total_amount || "",
        gst_number: res.data.gst_number || "",
        products: res.data.products || [],
        notes: res.data.notes || "",
        file_url: res.data.file_url || "",
        original_extracted_text: res.data.original_extracted_text || "",
        original_extracted_data: res.data
      });

      setOcrCompleted(true);
      toast.success("AI OCR extraction successful!");
    } catch (err) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      const errMsg = err.response?.data?.detail || "Failed to process OCR. Please make sure the image is clear.";
      toast.error(errMsg, { duration: 6000 });
    } finally {
      setIsProcessingOcr(false);
    }
  };

  // Add Product Row in Form
  const addProductRow = () => {
    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, { name: "", quantity: 1, purchase_price: 0 }]
    }));
  };

  // Remove Product Row in Form
  const removeProductRow = (idx) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== idx)
    }));
  };

  // Handle Product Field Changes
  const handleProductChange = (idx, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[idx][field] = value;
    setFormData((prev) => ({ ...prev, products: updatedProducts }));
  };

  // Save the Purchase Bill to DB
  const handleSaveBill = async (e) => {
    e.preventDefault();

    if (!formData.supplier_name || !formData.invoice_number || !formData.total_amount) {
      toast.error("Please fill in Supplier Name, Invoice Number, and Total Amount.");
      return;
    }

    try {
      let cleanDate = formData.bill_date;
      if (cleanDate && typeof cleanDate === "string") {
        cleanDate = cleanDate.trim();
        if (cleanDate.includes("/")) {
          const parts = cleanDate.split("/");
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              cleanDate = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            } else {
              cleanDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
          }
        }
      }

      const payload = {
        ...formData,
        bill_date: cleanDate || new Date().toISOString().slice(0, 10),
        total_amount: parseFloat(formData.total_amount) || 0,
        convert_to_inventory: convertInventory
      };

      await authAxios.post("/api/purchase/smart-bills/", payload);
      toast.success(
        convertInventory
          ? "Purchase bill saved and inventory stock synchronized successfully!"
          : "Purchase bill saved successfully!"
      );

      // Reset Form State
      setFormData({
        supplier_name: "",
        invoice_number: "",
        bill_date: "",
        total_amount: "",
        gst_number: "",
        products: [],
        notes: "",
        file_url: "",
        original_extracted_text: "",
        original_extracted_data: {}
      });
      setOcrCompleted(false);
      setUploadProgress(0);

      // Refresh Data
      fetchData();
    } catch (err) {
      console.error(err);
      let errMsg = "Failed to save purchase bill.";
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === "string") {
          errMsg = data;
        } else if (data.detail) {
          errMsg = data.detail;
        } else if (typeof data === "object") {
          const firstKey = Object.keys(data)[0];
          const errorVal = data[firstKey];
          if (Array.isArray(errorVal)) {
            errMsg = errorVal[0];
          } else if (typeof errorVal === "string") {
            errMsg = errorVal;
          } else {
            errMsg = JSON.stringify(errorVal);
          }
        }
      }
      toast.error(errMsg, { duration: 6000 });
    }
  };

  // Print Bill
  const handlePrint = (billUrl) => {
    const printWindow = window.open(billUrl, "_blank");
    printWindow.focus();
    printWindow.print();
  };

  // Download Bill
  const handleDownload = async (billUrl, invoiceNum) => {
    try {
      // Append download parameter to ensure backend sets Attachment header
      let targetUrl = billUrl;
      try {
        const urlObj = new URL(billUrl);
        urlObj.searchParams.append('download', 'true');
        targetUrl = urlObj.toString();
      } catch (err) {
        // Fallback if billUrl is a relative path
        if (targetUrl.includes('?')) {
          targetUrl += '&download=true';
        } else {
          targetUrl += '?download=true';
        }
      }

      const response = await fetch(targetUrl);
      if (!response.ok) {
        throw new Error(`Server returned status ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const localUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = localUrl;

      // Determine extension
      let extension = "jpeg";
      const contentType = response.headers.get("content-type");
      if (contentType) {
        if (contentType.includes("pdf")) {
          extension = "pdf";
        } else if (contentType.includes("png")) {
          extension = "png";
        } else if (contentType.includes("gif")) {
          extension = "gif";
        } else if (contentType.includes("sheet") || contentType.includes("excel")) {
          extension = "xlsx";
        }
      } else {
        const urlPath = billUrl.split('?')[0];
        const pathParts = urlPath.split('.');
        if (pathParts.length > 1) {
          extension = pathParts[pathParts.length - 1];
        }
      }

      link.download = `Invoice-${invoiceNum || "bill"}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(localUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download invoice. Please try again.");
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto dark:text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent">
            Smart Purchase Invoicing
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-semibold">
            AI-Powered Multi-Lingual OCR Bill Uploader & Stock Synchronizer
          </p>
        </div>

        {/* Global Save Button */}
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowUploadOptions(!showUploadOptions)}
            className="flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-2xl shadow-lg font-bold text-sm transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            Save Purchase Bill
          </motion.button>

          {/* Upload Choices Dropdown */}
          <AnimatePresence>
            {showUploadOptions && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute right-0 mt-3 w-56 bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 p-2 space-y-1"
              >
                <button
                  onClick={() => triggerUpload("camera")}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-left font-bold"
                >
                  <Camera className="w-4 h-4 text-blue-500" />
                  Take Photo
                </button>
                <button
                  onClick={() => triggerUpload("image")}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-left font-bold"
                >
                  <ImageIcon className="w-4 h-4 text-green-500" />
                  Upload Image
                </button>
                <button
                  onClick={() => triggerUpload("pdf")}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-left font-bold"
                >
                  <FileText className="w-4 h-4 text-red-500" />
                  Upload PDF
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*,application/pdf"
        className="hidden"
      />
      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileChange}
        accept="image/*"
        capture="environment"
        className="hidden"
      />

      {/* Dashboard Analytics Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          whileHover={{ y: -5 }}
          className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 p-6 rounded-3xl shadow-sm flex items-center gap-5 relative overflow-hidden"
        >
          <div className="p-4 bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 rounded-2xl">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
              Monthly Purchase Total
            </p>
            <h2 className="text-2xl font-black mt-1">
              ₹{analytics.monthly_purchase_total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </h2>
          </div>
          <div className="absolute right-0 bottom-0 opacity-5 dark:opacity-10 text-green-600">
            <TrendingUp className="w-32 h-32 translate-x-6 translate-y-6" />
          </div>
        </motion.div>

        <motion.div
          whileHover={{ y: -5 }}
          className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 p-6 rounded-3xl shadow-sm flex items-center gap-5 relative overflow-hidden"
        >
          <div className="p-4 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-2xl">
            <Store className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
              Most Used Supplier
            </p>
            <h2 className="text-xl font-black mt-1 truncate max-w-[200px]">
              {analytics.most_used_supplier}
            </h2>
          </div>
          <div className="absolute right-0 bottom-0 opacity-5 dark:opacity-10 text-blue-600">
            <Store className="w-32 h-32 translate-x-6 translate-y-6" />
          </div>
        </motion.div>

        <motion.div
          whileHover={{ y: -5 }}
          className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 p-6 rounded-3xl shadow-sm flex items-center gap-5 relative overflow-hidden"
        >
          <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl">
            <FileSpreadsheet className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
              Total Uploaded Bills
            </p>
            <h2 className="text-2xl font-black mt-1">
              {analytics.total_uploaded_bills}
            </h2>
          </div>
          <div className="absolute right-0 bottom-0 opacity-5 dark:opacity-10 text-emerald-600">
            <FileSpreadsheet className="w-32 h-32 translate-x-6 translate-y-6" />
          </div>
        </motion.div>
      </div>

      {/* Main Drag & Drop Zone */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-3 border-dashed rounded-3xl p-8 transition-all duration-300 flex flex-col items-center justify-center text-center cursor-pointer ${
              isDragging
                ? "border-green-500 bg-green-50/50 dark:bg-green-500/10"
                : "border-gray-200 dark:border-white/10 bg-white dark:bg-dark-sidebar hover:border-green-500 dark:hover:border-green-500"
            }`}
            onClick={() => triggerUpload("image")}
          >
            <div className="p-5 bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 rounded-3xl mb-4">
              <Upload className="w-8 h-8 animate-bounce" />
            </div>
            <h3 className="font-bold text-lg">Drag & Drop Purchase Bill here</h3>
            <p className="text-sm text-gray-400 mt-1">
              Supports JPEG, PNG, or PDF files up to 10MB
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full text-xs font-bold text-gray-500">English Text</span>
              <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full text-xs font-bold text-gray-500">Tamil printed text</span>
              <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full text-xs font-bold text-gray-500">Handwritten Tamil notes</span>
            </div>

            {/* Upload Progress Bar */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full max-w-md mt-6 space-y-2">
                <div className="flex justify-between text-xs font-bold">
                  <span>Uploading Invoice...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-green-500 to-emerald-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* OCR Pulse / Laser Loader */}
          {isProcessingOcr && (
            <div className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 p-8 rounded-3xl flex flex-col items-center justify-center relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-green-500 to-emerald-500 animate-pulse" />
              <Loader2 className="w-10 h-10 text-green-500 animate-spin mb-4" />
              <h3 className="font-bold text-lg">AI Scanning & Analyzing Invoice Details...</h3>
              <p className="text-sm text-gray-400 mt-1 max-w-md text-center">
                Extracted shop name, products, tax details, and matching with inventory stock batch codes.
              </p>
              {/* Laser line scanning simulation */}
              <div className="w-full max-w-sm h-32 border border-gray-100 dark:border-white/5 rounded-2xl mt-6 relative overflow-hidden bg-gray-50/50 dark:bg-black/25">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/10 to-transparent w-full h-1/2 animate-[scan_2s_infinite_ease-in-out]" />
                <div className="flex flex-col gap-2 p-4 text-[10px] font-mono text-gray-400">
                  <div>[OCR] DETECTED: GSTIN 33AAAAA...</div>
                  <div>[OCR] DETECTED: Ponni Rice 5kg...</div>
                  <div>[OCR] DETECTED: சமையல் எண்ணெய்...</div>
                  <div>[OCR] DETECTED: பில் சரிபார்க்கப்பட்டது...</div>
                </div>
              </div>
            </div>
          )}

          {/* Smart Form Display */}
          {ocrCompleted && (
            <motion.form
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleSaveBill}
              className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 p-6 rounded-3xl shadow-sm space-y-6"
            >
              {/* AI Verification Banner */}
              <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 rounded-2xl flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-500 mt-0.5 animate-pulse" />
                <div>
                  <h4 className="font-bold text-blue-800 dark:text-blue-400 text-sm">
                    AI detected these details. Please verify before saving.
                  </h4>
                  <p className="text-xs text-blue-600/80 dark:text-blue-300/80 mt-0.5">
                    Extracted from the uploaded bill image. You can correct fields or update the product grid directly.
                  </p>
                </div>
              </div>

              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Shop / Supplier Name</label>
                  <input
                    type="text"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    placeholder="e.g. Velan Stores"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Invoice Number</label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    placeholder="e.g. INV-9098"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Invoice Date</label>
                  <input
                    type="date"
                    value={formData.bill_date}
                    onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">GST Number</label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    placeholder="GSTIN format"
                  />
                </div>

                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-400 uppercase">Total Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Products Sub-table */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-gray-400 uppercase">Extracted Products</label>
                  <button
                    type="button"
                    onClick={addProductRow}
                    className="flex items-center gap-1 text-xs font-bold text-green-600 dark:text-green-400 hover:text-green-700"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Product
                  </button>
                </div>

                <div className="border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-white/5 text-[10px] font-black uppercase text-gray-400 tracking-wider">
                        <th className="p-3">Product Name</th>
                        <th className="p-3 w-28">Quantity</th>
                        <th className="p-3 w-32">Purchase Price (₹)</th>
                        <th className="p-3 w-16 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                      {formData.products.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-white/3">
                          <td className="p-2">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => handleProductChange(idx, "name", e.target.value)}
                              className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50"
                              placeholder="Product description"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => handleProductChange(idx, "quantity", e.target.value)}
                              className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.01"
                              value={item.purchase_price}
                              onChange={(e) => handleProductChange(idx, "purchase_price", e.target.value)}
                              className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50"
                            />
                          </td>
                          <td className="p-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeProductRow(idx)}
                              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {formData.products.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-400 text-xs italic">
                            No products extracted yet. Click "Add Product" to add manually.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes Field */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase">Handwritten / Extracted Notes</label>
                <textarea
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  placeholder="Notes, references, or instructions..."
                />
              </div>

              {/* Toggle option for Convert to Inventory */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="convertInventory"
                    checked={convertInventory}
                    onChange={(e) => setConvertInventory(e.target.checked)}
                    className="w-4.5 h-4.5 rounded border-gray-200 dark:border-white/10 text-green-600 focus:ring-green-500"
                  />
                  <div>
                    <label htmlFor="convertInventory" className="text-sm font-bold block cursor-pointer">
                      Convert to Inventory Stock
                    </label>
                    <span className="text-[11px] text-gray-400 block mt-0.5">
                      Check this to automatically add extracted products to inventory stock and update purchase price.
                    </span>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setOcrCompleted(false);
                    setUploadProgress(0);
                  }}
                  className="px-5 py-3 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl font-bold text-sm"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl shadow font-bold text-sm flex items-center gap-2"
                >
                  <Check className="w-4 h-4" /> Save Verified Bill
                </button>
              </div>
            </motion.form>
          )}
        </div>

        {/* Sidebar Info Section */}
        <div className="space-y-6">
          {/* Info Card */}
          <div className="bg-gradient-to-br from-green-900 to-emerald-800 text-white p-6 rounded-3xl shadow-sm relative overflow-hidden">
            <h3 className="text-lg font-black flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
              Gio Smart OCR Engine
            </h3>
            <p className="text-xs text-green-100 mt-2 leading-relaxed">
              Upload any purchase invoice or supplier receipt. Our multi-lingual engine automatically scans, processes, and extracts all vital transaction keys inside printed English, printed Tamil, and custom handwritten notes.
            </p>
            <div className="mt-4 p-3 bg-white/10 rounded-2xl flex items-start gap-2 text-[10px]">
              <Info className="w-4 h-4 text-yellow-300 mt-0.5" />
              <span>
                Tip: Snap high-resolution pictures or use digital PDFs for ultra-accurate 99.8% auto-extraction accuracy.
              </span>
            </div>
          </div>

          {/* OCR Showcase Scenarios */}
          <div className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 p-6 rounded-3xl shadow-sm space-y-4">
            <h4 className="font-bold text-sm uppercase text-gray-400 tracking-wider">
              OCR Showcase Scenarios
            </h4>
            <p className="text-xs text-gray-400">
              Try uploading mock files named with these keywords to experience the fully verified multilingual extraction:
            </p>
            <div className="space-y-2 text-xs">
              <div className="p-3 bg-gray-50 dark:bg-white/5 rounded-2xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer" onClick={() => triggerUpload("image")}>
                <div>
                  <span className="font-bold text-gray-700 dark:text-gray-200 block">Velan Stores Receipt</span>
                  <span className="text-[10px] text-gray-400 block mt-0.5">Tamil + English Printed + Handwriting</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>

              <div className="p-3 bg-gray-50 dark:bg-white/5 rounded-2xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer" onClick={() => triggerUpload("image")}>
                <div>
                  <span className="font-bold text-gray-700 dark:text-gray-200 block">Kurinji Traders Bill</span>
                  <span className="text-[10px] text-gray-400 block mt-0.5">Handwritten Tamil verified stamps</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill List Table / Filters & Search */}
      <div className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/5 rounded-3xl p-6 shadow-sm space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 dark:border-white/5 pb-6">
          <div>
            <h2 className="text-xl font-black">Scanned Purchase Bills History</h2>
            <p className="text-gray-400 text-xs mt-0.5 font-bold">
              Review and audit all AI processed bills and inventory conversions.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Search */}
            <div className="relative flex-1 md:flex-initial">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by supplier name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full md:w-64 pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2.5 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/15 rounded-xl text-gray-600 dark:text-gray-300 flex items-center gap-2 text-sm font-bold transition-all border border-transparent hover:border-gray-200 dark:hover:border-white/10"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>

        {/* Extended Filters Dropdown */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden"
            >
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Date</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-transparent text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 1500"
                  value={filterAmount}
                  onChange={(e) => setFilterAmount(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-transparent text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider">GST Number</label>
                <input
                  type="text"
                  placeholder="Filter by GSTIN"
                  value={filterGst}
                  onChange={(e) => setFilterGst(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-transparent text-xs"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilterDate("");
                    setFilterAmount("");
                    setFilterGst("");
                  }}
                  className="w-full py-2 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Clear Filters
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bills Table */}
        <div className="border border-gray-100 dark:border-white/5 rounded-3xl overflow-hidden bg-white dark:bg-dark-sidebar shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-white/5 text-xs font-black uppercase text-gray-400 tracking-wider">
                <th className="p-4">Bill ID</th>
                <th className="p-4">Supplier</th>
                <th className="p-4">Invoice Number</th>
                <th className="p-4">Bill Date</th>
                <th className="p-4">GST Number</th>
                <th className="p-4 text-right">Total Amount (₹)</th>
                <th className="p-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/5 text-sm">
              {bills.map((bill) => (
                <tr key={bill.id} className="hover:bg-gray-50/50 dark:hover:bg-white/3 transition-colors">
                  <td className="p-4 font-bold text-green-600 dark:text-green-400">
                    {bill.bill_id}
                  </td>
                  <td className="p-4 font-semibold">
                    {bill.supplier_name}
                  </td>
                  <td className="p-4 text-gray-500 font-medium">
                    {bill.invoice_number}
                  </td>
                  <td className="p-4 text-gray-500 font-medium">
                    {bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : "N/A"}
                  </td>
                  <td className="p-4 text-gray-400 text-xs font-mono">
                    {bill.gst_number || "N/A"}
                  </td>
                  <td className="p-4 text-right font-black text-gray-900 dark:text-white">
                    ₹{parseFloat(bill.total_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => setPreviewBill(bill)}
                        className="p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-gray-600 dark:text-gray-300 transition-all border border-gray-100 dark:border-transparent"
                        title="View Bill Details"
                      >
                        <Eye className="w-4.5 h-4.5" />
                      </button>
                      {bill.file_url && (
                        <>
                          <button
                            onClick={() => handleDownload(resolveMediaUrl(bill.file_url), bill.invoice_number)}
                            className="p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-blue-500 transition-all border border-gray-100 dark:border-transparent"
                            title="Download Original"
                          >
                            <Download className="w-4.5 h-4.5" />
                          </button>
                          <button
                            onClick={() => handlePrint(resolveMediaUrl(bill.file_url))}
                            className="p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-green-500 transition-all border border-gray-100 dark:border-transparent"
                            title="Print Invoice"
                          >
                            <Printer className="w-4.5 h-4.5" />
                          </button>
                        </>
                      )}

                      {/* Generate / View Purchase Invoice */}
                      {bill.is_invoice_generated ? (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-0.5 bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 rounded-full text-[9px] font-black uppercase tracking-wider">
                            Generated
                          </span>
                          <button
                            onClick={() => viewGeneratedInvoice(bill)}
                            className="p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl text-purple-600 dark:text-purple-400 transition-all border border-gray-100 dark:border-transparent"
                            title="View Purchase Invoice"
                          >
                            <FileText className="w-4.5 h-4.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => openGenerateInvoiceModal(bill)}
                          className="px-2.5 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-[10px] font-bold shadow flex items-center gap-1 transition-all"
                          title="Generate Purchase Invoice"
                        >
                          <Sparkles className="w-3 h-3 animate-pulse" />
                          <span>Gen Invoice</span>
                        </button>
                      )}

                      {/* Delete Purchase Bill Record */}
                      <button
                        onClick={() => handleDeleteBill(bill)}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl text-red-500 transition-all border border-gray-100 dark:border-transparent"
                        title="Delete Scanned Record"
                      >
                        <Trash2 className="w-4.5 h-4.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {bills.length === 0 && !loadingBills && (
                <tr>
                  <td colSpan="7" className="p-6 text-center">
                    <EmptyState
                      preset="invoices"
                      title="No purchase bills uploaded yet"
                      message="Click 'Save Purchase Bill' at the top to upload your first receipt!"
                      className="py-8"
                    />
                  </td>
                </tr>
              )}
              {loadingBills && (
                <tr>
                  <td colSpan="7" className="p-6 text-center">
                    <EmptyState
                      preset="invoices"
                      title="Loading records..."
                      message="Please wait a moment while we fetch your purchase bills history."
                      className="py-8"
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bill Preview Modal */}
      <AnimatePresence>
        {previewBill && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/10 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl relative"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5">
                <div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white">
                    {previewBill.bill_id} - {previewBill.supplier_name}
                  </h2>
                  <p className="text-xs text-gray-400 font-bold mt-0.5">
                    Uploaded on: {new Date(previewBill.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setPreviewBill(null);
                    setZoomScale(1);
                  }}
                  className="p-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Extracted Details & Products */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-black uppercase text-gray-400 tracking-wider mb-3">Extracted Details</h3>
                    <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-4 space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-semibold">Supplier Name:</span>
                        <span className="font-bold">{previewBill.supplier_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-semibold">Invoice Number:</span>
                        <span className="font-bold">{previewBill.invoice_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-semibold">Bill Date:</span>
                        <span className="font-bold">{previewBill.bill_date ? new Date(previewBill.bill_date).toLocaleDateString() : "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 font-semibold">GSTIN:</span>
                        <span className="font-mono font-bold text-xs">{previewBill.gst_number || "N/A"}</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-100 dark:border-white/5 pt-3">
                        <span className="text-gray-400 font-bold">Total Amount:</span>
                        <span className="font-black text-green-600 dark:text-green-400">
                          ₹{parseFloat(previewBill.total_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-black uppercase text-gray-400 tracking-wider mb-3">Products</h3>
                    <div className="border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-white/5 text-[10px] font-black uppercase text-gray-400">
                            <th className="p-3">Product Name</th>
                            <th className="p-3 text-center">Quantity</th>
                            <th className="p-3 text-right">Price (₹)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                          {previewBill.products.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-white/3">
                              <td className="p-3 font-semibold">{item.name}</td>
                              <td className="p-3 text-center">{parseFloat(item.quantity)}</td>
                              <td className="p-3 text-right">₹{parseFloat(item.purchase_price).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {previewBill.notes && (
                    <div>
                      <h3 className="text-sm font-black uppercase text-gray-400 tracking-wider mb-2">Notes</h3>
                      <div className="p-4 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 rounded-2xl text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                        {previewBill.notes}
                      </div>
                    </div>
                  )}
                </div>

                {/* Uploaded File View & Zoom / Download */}
                <div className="flex flex-col space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-black uppercase text-gray-400 tracking-wider">Uploaded Receipt</h3>
                    <div className="flex items-center gap-1 bg-gray-50 dark:bg-white/5 p-1 rounded-xl">
                      <button
                        onClick={() => setZoomScale((prev) => Math.max(0.5, prev - 0.2))}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg text-gray-500"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <span className="text-[10px] font-bold px-1">{Math.round(zoomScale * 100)}%</span>
                      <button
                        onClick={() => setZoomScale((prev) => Math.min(2, prev + 0.2))}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg text-gray-500"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="border border-gray-100 dark:border-white/5 rounded-2xl flex-1 h-[400px] overflow-auto flex items-center justify-center bg-gray-50 dark:bg-black/50 p-4">
                    {previewBill.file_url?.toLowerCase().endsWith(".pdf") ? (
                      <div className="text-center space-y-4">
                        <FileText className="w-16 h-16 text-red-500 mx-auto" />
                        <span className="text-xs font-bold text-gray-400 block">PDF Document Preview</span>
                        <a
                          href={resolveMediaUrl(previewBill.file_url)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block px-4 py-2 bg-red-500 text-white font-bold rounded-xl text-xs hover:bg-red-600 transition"
                        >
                          View PDF in new tab
                        </a>
                      </div>
                    ) : (
                      <motion.img
                        src={resolveMediaUrl(previewBill.file_url)}
                        alt="Bill Original Invoice"
                        style={{ scale: zoomScale }}
                        className="max-h-full max-w-full object-contain rounded-lg transition-transform duration-200"
                      />
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownload(resolveMediaUrl(previewBill.file_url), previewBill.invoice_number)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl text-xs shadow transition-colors"
                    >
                      <Download className="w-4 h-4" /> Download Original Bill
                    </button>
                    <button
                      onClick={() => handlePrint(resolveMediaUrl(previewBill.file_url))}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl text-xs shadow transition-colors"
                    >
                      <Printer className="w-4 h-4" /> Print Document
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Generate Purchase Invoice Modal */}
      <AnimatePresence>
        {invoiceModalBill && invoiceForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/10 rounded-3xl w-full max-w-5xl my-8 overflow-hidden flex flex-col shadow-2xl relative"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-2xl">
                    <Sparkles className="w-6 h-6 animate-pulse" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-gray-900 dark:text-white">
                      Generate Purchase Invoice
                    </h2>
                    <p className="text-xs text-gray-400 font-bold mt-0.5">
                      Verify & finalize invoice information extracted from scanned bill ID: {invoiceModalBill.bill_id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setInvoiceModalBill(null)}
                  className="p-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleGenerateInvoiceSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                
                {/* 2-Column Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Supplier Card */}
                  <div className="md:col-span-2 bg-gray-50 dark:bg-white/5 p-6 rounded-3xl border border-gray-100 dark:border-white/5 space-y-4">
                    <h3 className="text-xs font-black uppercase text-purple-600 dark:text-purple-400 tracking-wider">
                      Supplier & Tax Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase">Supplier Name</label>
                        <input
                          type="text"
                          required
                          value={invoiceForm.supplier_name}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, supplier_name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black/20 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
                          placeholder="e.g. Velan Stores"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase">GST Number</label>
                        <input
                          type="text"
                          value={invoiceForm.gst_number}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, gst_number: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black/20 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm font-mono"
                          placeholder="GSTIN format"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Invoice Header Details */}
                  <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-3xl border border-gray-100 dark:border-white/5 space-y-4">
                    <h3 className="text-xs font-black uppercase text-purple-600 dark:text-purple-400 tracking-wider">
                      Bill Metadata
                    </h3>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase">Invoice / Bill Number</label>
                        <input
                          type="text"
                          required
                          value={invoiceForm.invoice_number}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black/20 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-xs"
                          placeholder="e.g. BILL-123"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase">Bill Date</label>
                        <input
                          type="date"
                          required
                          value={invoiceForm.bill_date}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, bill_date: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black/20 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scanned Products Table */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xs font-black uppercase text-purple-600 dark:text-purple-400 tracking-wider">
                      Invoice Items Breakdown
                    </h3>
                    <button
                      type="button"
                      onClick={addInvoiceItem}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 dark:bg-purple-500/10 dark:hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 rounded-xl text-xs font-bold transition-all"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Product Item
                    </button>
                  </div>

                  <div className="border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden shadow-inner">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-white/5 text-[10px] font-black uppercase text-gray-400 tracking-wider border-b border-gray-100 dark:border-white/5">
                          <th className="p-3.5">Product Name</th>
                          <th className="p-3.5 w-24 text-center">Qty</th>
                          <th className="p-3.5 w-32 text-right">Unit Price (₹)</th>
                          <th className="p-3.5 w-24 text-center">GST %</th>
                          <th className="p-3.5 w-28 text-right">Line Total (₹)</th>
                          <th className="p-3.5 w-16 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                        {invoiceForm.products.map((item, idx) => {
                          const lineVal = (parseFloat(item.quantity) || 0) * (parseFloat(item.purchase_price) || 0);
                          return (
                            <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-white/3 transition-colors">
                              <td className="p-2">
                                <input
                                  type="text"
                                  required
                                  value={item.name}
                                  onChange={(e) => handleInvoiceItemChange(idx, "name", e.target.value)}
                                  className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50 font-semibold"
                                  placeholder="e.g. Basmati Rice"
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  required
                                  step="0.001"
                                  value={item.quantity}
                                  onChange={(e) => handleInvoiceItemChange(idx, "quantity", e.target.value)}
                                  className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50 text-center"
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  required
                                  step="0.01"
                                  value={item.purchase_price}
                                  onChange={(e) => handleInvoiceItemChange(idx, "purchase_price", e.target.value)}
                                  className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50 text-right"
                                />
                              </td>
                              <td className="p-2">
                                <select
                                  value={item.tax_rate}
                                  onChange={(e) => handleInvoiceItemChange(idx, "tax_rate", e.target.value)}
                                  className="w-full px-2 py-1.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-white/10 bg-transparent focus:bg-white dark:focus:bg-black/50 text-center"
                                >
                                  <option value="0">0%</option>
                                  <option value="5">5%</option>
                                  <option value="12">12%</option>
                                  <option value="18">18%</option>
                                  <option value="28">28%</option>
                                </select>
                              </td>
                              <td className="p-2 text-right font-black text-gray-700 dark:text-gray-200">
                                ₹{lineVal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="p-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeInvoiceItem(idx)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {invoiceForm.products.length === 0 && (
                          <tr>
                            <td colSpan="6" className="p-8 text-center text-gray-400 text-xs italic">
                              No items in the invoice. Click "Add Product Item" to add.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sub-details & Recalculated Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                  {/* Notes & Additional Text */}
                  <div className="md:col-span-2 space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase">Extracted / Custom Notes</label>
                    <textarea
                      rows="3"
                      value={invoiceForm.notes}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black/20 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-xs"
                      placeholder="Add terms, notes, or references..."
                    />
                  </div>

                  {/* Pricing Breakdown Summary Card */}
                  <div className="bg-purple-50/50 dark:bg-purple-500/5 border border-purple-100/60 dark:border-purple-500/10 p-5 rounded-3xl space-y-3 shadow-sm">
                    <h4 className="text-[10px] font-black uppercase text-purple-700 dark:text-purple-400 tracking-wider">
                      Financial Recalculation
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between font-semibold">
                        <span className="text-gray-400">Total Items Value:</span>
                        <span>
                          ₹{invoiceForm.products.reduce((acc, item) => acc + ((parseFloat(item.quantity) || 0) * (parseFloat(item.purchase_price) || 0)), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span className="text-gray-400">Tax Component:</span>
                        <span>
                          ₹{invoiceForm.products.reduce((acc, item) => {
                            const val = (parseFloat(item.quantity) || 0) * (parseFloat(item.purchase_price) || 0);
                            return acc + (val * ((parseFloat(item.tax_rate) || 0) / 100));
                          }, 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="border-t border-purple-200/50 dark:border-purple-500/20 my-2 pt-2 flex justify-between items-center text-sm font-black">
                        <span className="text-purple-900 dark:text-purple-300">Grand Total:</span>
                        <span className="text-purple-700 dark:text-purple-400 text-lg">
                          ₹{parseFloat(invoiceForm.total_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-white/5">
                  <button
                    type="button"
                    onClick={() => setInvoiceModalBill(null)}
                    className="px-5 py-3 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl font-bold text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={generatingInvoice}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl shadow-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {generatingInvoice ? (
                      <>
                        <Loader2 className="w-4.5 h-4.5 animate-spin" />
                        <span>Generating Invoice...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Generate Purchase Invoice</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmBill && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/10 rounded-3xl w-full max-w-md p-6 shadow-2xl relative overflow-hidden"
            >
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 mx-auto mb-5 shadow-sm border border-red-500/10">
                <Trash2 className="w-6 h-6" />
              </div>
              
              <h2 className="text-xl font-black text-gray-900 dark:text-white text-center mb-2">
                Delete Purchase Bill?
              </h2>
              
              <p className="text-sm text-gray-500 dark:text-slate-400 text-center mb-6 font-medium leading-relaxed">
                Are you sure you want to delete the purchase bill record <span className="font-extrabold text-red-500">{deleteConfirmBill.bill_id}</span> from <span className="font-bold text-gray-800 dark:text-white">{deleteConfirmBill.supplier_name || 'Unknown'}</span>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmBill(null)}
                  className="flex-1 py-3 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white rounded-xl text-xs font-black uppercase tracking-wider transition duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteBill}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-red-600/10 hover:shadow-red-700/20 transition duration-150"
                >
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
